import React from 'react';
import { Tabs } from 'antd';
import { Resolutions } from '../../types/banner';
import { LegacyAdChannels } from '../../types/ad-channels';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { DEFAULT_CHANNEL_CONFIGS, RESOLUTION_CATEGORIES, RESOLUTION_DISPLAY_NAMES } from '../../constants/channels';
import { Pill } from '@doer/frontend-components/pill';

interface LegacyChannelTabsProps {
  resolutions: string[];
  selectedResolution: string | null;
  onResolutionChange: (resolution: string) => void;
  selectedCategory: LegacyAdChannels;
  onCategoryChange: (category: LegacyAdChannels) => void;
}

export const LegacyChannelTabs: React.FC<LegacyChannelTabsProps> = ({
  resolutions,
  selectedResolution,
  onResolutionChange,
  selectedCategory,
  onCategoryChange
}) => {
  const { fontSize, spacing } = useResponsiveSizes();

  // Categorizar resoluciones
  const categorizedResolutions = React.useMemo(() => {
    const categories: Record<LegacyAdChannels, Resolutions[]> = {
      social: [],
      display: [],
      DOOH: []
    };

    resolutions.forEach(res => {
      const channelsList = RESOLUTION_CATEGORIES[res as Resolutions];
      if (channelsList) {
        channelsList.forEach(channel => {
          categories[channel].push(res as Resolutions);
        });
      }
    });

    return categories;
  }, [resolutions]);

  const tabItems = (Object.keys(categorizedResolutions) as LegacyAdChannels[])
    .filter(category => categorizedResolutions[category].length > 0)
    .map(category => {
      const config = DEFAULT_CHANNEL_CONFIGS[category];

      return {
        key: category,
        label: <span style={{ fontSize: fontSize.xlg, fontWeight: 500 }}>{config.label}</span>,
        children: (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', paddingTop: 8 }}>
            {categorizedResolutions[category].map(res => {
              let label = RESOLUTION_DISPLAY_NAMES[res] || res;

              // Special case: display category shows pixel resolution for 1920x1080
              if (selectedCategory === 'display' && res === '1920x1080') {
                label = '1920x1080';
              }

              const isSelected = selectedResolution === res;

              return (
                <Pill
                  key={res}
                  size='XS'
                  variant={isSelected ? 'outline' : 'ghost'}
                  onClick={() => onResolutionChange(res)}
                >
                  {label.replace(' | ', '')}
                </Pill>
              );
            })}
          </div>
        )
      };
    });

  return (
    <div style={{ width: '100%', marginBottom: spacing.lg }}>
      <Tabs
        activeKey={selectedCategory}
        onChange={key => {
          const newCategory = key as LegacyAdChannels;
          onCategoryChange(newCategory);
          if (!categorizedResolutions[newCategory].includes(selectedResolution as Resolutions)) {
            onResolutionChange(categorizedResolutions[newCategory][0]);
          }
        }}
        items={tabItems}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      />
    </div>
  );
};
